import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

interface ReleaseNote {
  id: string;
  version: string;
  platforms: string[];
  title: string;
  body: string;
  published: boolean;
  createdAt: string;
}

export function WhatsNewPage() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: ReleaseNote[] }>('/release-notes?platform=web');
      setNotes(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-2xl md:mx-auto">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        Back
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={20} className="text-plum" />
        <h1 className="font-display text-h1 text-midnight">What&apos;s New</h1>
      </div>
      <p className="text-[13px] text-slate mb-6">See what&apos;s changed in the latest updates.</p>

      {loading && (
        <div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && notes.length === 0 && (
        <div className="text-center py-16">
          <p className="text-small text-slate">No release notes yet.</p>
        </div>
      )}

      {!loading && !error && notes.length > 0 && (
        <div className="flex flex-col gap-5">
          {notes.map(note => (
            <div key={note.id} className="bg-white border border-stone rounded-xl shadow-brand-sm px-4 py-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[12px] font-mono font-semibold text-plum">v{note.version}</span>
                <span className="text-[11px] text-slate">
                  {new Date(note.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
              <h2 className="font-display text-h3 text-midnight mb-2">{note.title}</h2>
              {note.body && (
                <div className="text-[13px] text-slate leading-relaxed whitespace-pre-wrap">
                  {note.body}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
